import { isNonEmpty } from '@sgme/fp';
import { range } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { FICCGreek } from 'state/quotes/details/details.model';

export const useGreeksTitle = () => {
  const { formatNumber } = useIntl();
  return useCallback(
    (greeks: FICCGreek, greeksPrecision: number, selectedUnitIdx?: number) =>
      `${greeks.title} - ${greeks.type}=` +
      greeks.values
        ?.filter((_, i) => selectedUnitIdx === undefined || selectedUnitIdx === i)
        .map((value, i) => {
          const formattedVal = `${formatNumber(value, {
            maximumFractionDigits: greeksPrecision,
            minimumFractionDigits: greeksPrecision,
          })} ${greeks.units?.[i] ?? ''}`;
          return greeks.values?.length! > 1 && i === greeks.values?.length! - 1 ? `(${formattedVal})` : formattedVal;
        })
        .join(' '),
    [formatNumber],
  );
};

export const useGetSelectedUnitIdx = (selectedUnit: string, units: readonly string[]) => {
  return useMemo(() => {
    return units.indexOf(selectedUnit);
  }, [selectedUnit, units]);
};
const getEmptyRow = (matrix: (string | null)[][]): (string | null)[] => {
  return range(0, matrix[0].length).map(_ => null);
};

export const useFormatMatrix = (greek: FICCGreek, greeksPrecision: number) => {
  const isThreeAxisFICCGreeksEnabled = greek.matrix[0][0] === '';
  const formatGreeksTitle = useGreeksTitle();

  const getMatrixRows = useCallback(
    (selectedUnitIndex: number) => {
      const header = isThreeAxisFICCGreeksEnabled ? [...greek.matrix[0]] : [];
      const title = formatGreeksTitle(
        { ...greek, units: [greek.units[selectedUnitIndex]] },
        greeksPrecision,
        selectedUnitIndex,
      );
      const titleRow = getEmptyRow(greek.matrix);
      titleRow[0] = title;
      let content: (string | null)[][];
      if (isThreeAxisFICCGreeksEnabled) {
        const valuesColsCount = header.filter(isNonEmpty).length;
        const startingValuesIdx = header.length - valuesColsCount;
        content = greek.matrix.slice(1).map(row =>
          row.map((c, i) => {
            if (i >= startingValuesIdx && selectedUnitIndex !== undefined) {
              const valuesByUnit = c?.split(',');
              return valuesByUnit ? valuesByUnit[selectedUnitIndex] : c;
            }
            return c;
          }),
        );
      } else {
        content = greek.matrix.map(row =>
          row.filter((_, i) => {
            const startingValuesIdx = row.length - (greek.units.length ?? 1);
            return i < startingValuesIdx || i - startingValuesIdx === selectedUnitIndex;
          }),
        );
      }

      return { title: titleRow, header, content };
    },
    [isThreeAxisFICCGreeksEnabled, greek, formatGreeksTitle, greeksPrecision],
  );
  return getMatrixRows;
};
